import { useMemo, useState } from "react";
import { Combobox, InputDate, formatCurrency, getDate } from "medere-ui-framework";
import {
    HospitalizationDaysBudgetDetailDTO,
    defaultHospitalizationDaysBudgetDetailDTO,
    withDefaultCoveragePercentages,
} from "../../../dto/budget/BudgetDetailDTO";
import { NomenclatorDTO } from "../../../dto/NomenclatorDTO";
import { getNomenclatorsForHospitalizationDays } from "../../../servicesHooks/BillHospitalization/useBillHospitalizationService";
import {
    BILL_ITEM_CATEGORY_CONFIG,
    addDays,
    amountOf,
    canEditStayDays,
    getBillableUnits,
    getCategorySubtotal,
    getDuplicatedHealthPatientServices,
    getItemWarnings,
    getUnitsLabel,
    newBillableItem,
    recalculateItem,
    sortByHealthPatientService,
    toIsoDate,
    toWholeUnits,
    withStayDays,
    withUnitPrice,
} from "./billItems";
import { ContractContext, canValueByContract, fetchContractValue, hospitalizationDaysValue } from "./contractRevaluation";
import LoadTable, { LoadTableColumn, LoadTableRow } from "../../../components/novarium/LoadTable";
import NumberField from "./NumberField";
import { AddRowButton, FieldError, RowActions, WarningTags, formatApplicationDate, formatCoverage } from "./tableParts";

/** Tipo de detalle de los días de internación (estadía). */
const HOSPITALIZATION_DAYS_TYPE = 10;

type Row = LoadTableRow & { item: HospitalizationDaysBudgetDetailDTO; index: number };

interface HospitalizationDaysTabProps {
    items: HospitalizationDaysBudgetDetailDTO[];
    onChange: (items: HospitalizationDaysBudgetDetailDTO[]) => void;
    onEdit: (index: number) => void;
    contract: ContractContext;
    healthEntity: number | null | undefined;
    hospitalizationId: number | null | undefined;
}

interface AddDraft {
    stay: NomenclatorDTO | null;
    text: string;
    from: Date | null;
    days: number | null;
    price: number | null;
    contractValues: Partial<HospitalizationDaysBudgetDetailDTO> | null;
    /** El valor unitario lo escribió la persona: el del convenio no lo pisa. */
    priceEdited: boolean;
    errors: { stay?: string; from?: string; days?: string; price?: string };
}

const CONFIG = BILL_ITEM_CATEGORY_CONFIG.hospitalizationDays;

export default function HospitalizationDaysTab({ items, onChange, onEdit, contract, healthEntity, hospitalizationId }: HospitalizationDaysTabProps) {
    // Inicio de la estadía en el alta manual: si no se elige, la fecha de la factura.
    const emptyDraft = (): AddDraft => ({ stay: null, text: '', from: null, days: 1, price: null, contractValues: null, priceEdited: false, errors: {} });
    const [draft, setDraft] = useState<AddDraft>(emptyDraft);
    const stayFrom = draft.from ?? (contract.date ? getDate(contract.date) : null);

    // Sin internación (paciente solo quirúrgico) no hay estadía que facturar.
    const hasHospitalization = Boolean(hospitalizationId && hospitalizationId > 0);
    const selectionEnabled = canValueByContract(contract) && hasHospitalization;
    const disabledReason = !hasHospitalization
        ? 'El paciente no tiene internación en el período'
        : 'Elegí obra social, plan y fecha primero';

    const duplicated = useMemo(() => getDuplicatedHealthPatientServices(items), [items]);
    const rows: Row[] = useMemo(
        () => sortByHealthPatientService(items).map(({ item, index }) => ({ rowKey: `${index}-${item.nomenclator}`, item, index })),
        [items]
    );

    const update = (index: number, item: HospitalizationDaysBudgetDetailDTO) =>
        onChange(items.map((current, i) => i === index ? recalculateItem('hospitalizationDays', item) : current));

    const selectStay = async (stay: NomenclatorDTO | null) => {
        if (!stay?.nomenclator) {
            setDraft(prev => ({ ...prev, stay: null, text: stay?.codeanddescription ?? '', contractValues: null }));
            return;
        }
        setDraft(prev => ({ ...prev, stay, text: stay.codeanddescription ?? '', priceEdited: false, errors: { ...prev.errors, stay: undefined } }));
        const value = await fetchContractValue(stay.nomenclator, contract);
        const contractValues = hospitalizationDaysValue(value);
        setDraft(prev => prev.stay?.nomenclator === stay.nomenclator
            ? { ...prev, contractValues, price: prev.priceEdited ? prev.price : contractValues.unitprice ?? 0, errors: { ...prev.errors, price: undefined } }
            : prev);
    };

    const add = () => {
        const errors: AddDraft['errors'] = {};
        if (!draft.stay?.nomenclator) errors.stay = 'Elegí una estadía';
        const from = toIsoDate(stayFrom);
        if (!from) errors.from = 'Completá la fecha desde';
        const days = toWholeUnits(draft.days);
        if (days <= 0) errors.days = 'Ingresá los días';
        if (!draft.price || draft.price <= 0) errors.price = 'Ingresá el valor unitario';
        // La estadía se registra contra la internación y un período: sin eso no se puede descontar después.
        if (!hospitalizationId || hospitalizationId <= 0) errors.stay = disabledReason;
        if (Object.values(errors).some(Boolean)) {
            setDraft(prev => ({ ...prev, errors }));
            return;
        }
        const base: HospitalizationDaysBudgetDetailDTO = {
            ...defaultHospitalizationDaysBudgetDetailDTO,
            ...draft.contractValues,
            nomenclator: draft.stay!.nomenclator!,
            description: draft.stay!.codeanddescription ?? '',
            type: HOSPITALIZATION_DAYS_TYPE,
            unitsqty: days,
            applicationdate: from,
            billableitems: [newBillableItem(days, { hospitalization: hospitalizationId, datefrom: from, dateto: addDays(from as string, days) })],
        };
        const item = withDefaultCoveragePercentages(withUnitPrice('hospitalizationDays', base, draft.price!));
        onChange([...items, recalculateItem('hospitalizationDays', item)]);
        setDraft(emptyDraft());
    };

    const draftTotal = draft.stay && draft.price ? formatCurrency(amountOf(toWholeUnits(draft.days), draft.price).toNumber()) : '—';

    const columns: LoadTableColumn<Row>[] = [
        {
            key: 'stay',
            header: CONFIG.itemHeader,
            className: 'nv-col-item',
            addCell: (
                <div className="nv-cell-field">
                    <Combobox
                        getItems={getNomenclatorsForHospitalizationDays}
                        params={{ hospitalizationId, healthEntity, serviceDate: contract.date, orderByNomenclatorType: true }}
                        optionLabel="codeanddescription"
                        placeholder={selectionEnabled ? 'Buscá por código o nombre' : disabledReason}
                        minPanelWidth={600}
                        maxPanelWidth={800}
                        value={draft.text}
                        setValue={(e: NomenclatorDTO | null) => { void selectStay(e); }}
                        disable={!selectionEnabled}
                        forceSelection={false}
                        error={Boolean(draft.errors.stay)}
                        caption={draft.errors.stay}
                    />
                </div>
            ),
            body: ({ item }) => <span className="nv-truncate nv-tip" data-pr-tooltip={item.description}>{item.description}</span>,
        },
        {
            key: 'from',
            header: 'Desde',
            align: 'center',
            className: 'nv-col-date',
            addCell: (
                <div className="nv-cell-field">
                    <InputDate
                        value={stayFrom}
                        onChange={(e: { value?: Date | null }) => setDraft(prev => ({ ...prev, from: e.value ?? null, errors: { ...prev.errors, from: undefined } }))}
                        placeholder="Desde"
                        disable={!selectionEnabled}
                        error={Boolean(draft.errors.from)}
                        caption={draft.errors.from}
                        className="nv-cell-date"
                    />
                </div>
            ),
            body: ({ item }) => formatApplicationDate(item.applicationdate),
        },
        {
            key: 'days',
            header: CONFIG.quantityHeader,
            align: 'center',
            className: 'nv-col-qty',
            addCell: (
                <div className="nv-cell-field">
                    <NumberField kind="integer" inCell ariaLabel="Días de la estadía nueva" value={draft.days} min={0}
                        invalid={Boolean(draft.errors.days)} describedBy="days-add-days-error"
                        onCommit={(days) => setDraft(prev => ({ ...prev, days, errors: { ...prev.errors, days: undefined } }))} />
                    <FieldError id="days-add-days-error" message={draft.errors.days} />
                </div>
            ),
            body: ({ item, index }) => {
                const units = getUnitsLabel(item);
                if (canEditStayDays(item)) {
                    return (
                        <NumberField kind="integer" inCell ariaLabel={`Días de ${item.description}`} value={getBillableUnits(item)} min={1}
                            onCommit={(days) => update(index, withStayDays(item, Math.max(1, toWholeUnits(days))))} />
                    );
                }
                return <span className="nv-tip" data-pr-tooltip={`${units.detail}. Tiene varios períodos o estados: se cambian desde Editar estadía.`}>{units.text}</span>;
            },
        },
        {
            key: 'price',
            header: 'Valor unitario',
            align: 'right',
            className: 'nv-col-money',
            addCell: (
                <div className="nv-cell-field">
                    <NumberField kind="currency" inCell ariaLabel="Valor unitario de la estadía nueva" value={draft.price} min={0}
                        invalid={Boolean(draft.errors.price)} describedBy="days-add-price-error"
                        onCommit={(price) => setDraft(prev => ({ ...prev, price, priceEdited: true, errors: { ...prev.errors, price: undefined } }))} />
                    <FieldError id="days-add-price-error" message={draft.errors.price} />
                </div>
            ),
            body: ({ item, index }) => (
                <NumberField kind="currency" inCell ariaLabel={`Valor unitario de ${item.description}`} value={item.unitprice} min={0}
                    onCommit={(price) => update(index, withUnitPrice('hospitalizationDays', item, price))} />
            ),
        },
        {
            key: 'coverage',
            header: 'Cobertura OS',
            align: 'center',
            className: 'nv-col-pct',
            addCell: <span className="nv-muted">{formatCoverage(draft.contractValues?.healthentitypercentagecoverage)}</span>,
            body: ({ item }) => formatCoverage(item.healthentitypercentagecoverage),
        },
        {
            key: 'total',
            header: 'Total',
            align: 'right',
            className: 'nv-col-money',
            addCell: <span className="nv-strong">{draftTotal}</span>,
            body: ({ item }) => <span className="nv-strong">{formatCurrency(item.valuemonth ?? 0)}</span>,
        },
        {
            key: 'status',
            header: 'Estado',
            className: 'nv-col-status',
            body: ({ item }) => <WarningTags warnings={getItemWarnings('hospitalizationDays', item, duplicated)} />,
        },
    ];

    return (
        <LoadTable
            rows={rows}
            columns={columns}
            addAction={<AddRowButton label="Agregar estadía" onClick={add} />}
            rowActions={({ item, index }) => (
                <RowActions
                    itemName={item.description}
                    editLabel="Editar estadía"
                    onEdit={() => onEdit(index)}
                    onRemove={() => onChange(items.filter((_, i) => i !== index))}
                />
            )}
            emptyMessage={CONFIG.emptyMessage}
            footerValues={{ total: formatCurrency(getCategorySubtotal(items).toNumber()) }}
        />
    );
}
